import { Dispatch, memo, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { FCX, ID, includesInLC, startsFromInLC } from "@datassential/platform-ui-lib";
import { TableColumnsContext } from "@components/Table/TableColumns";
import cn from "classnames";
import { VirtualizedTable } from "@components/Table";
import { useVisualizeTableConfig, VisualizeTableConfig } from "@pages/Visualize/VisualizeTableConfig";
import { VisualizeData, VisualizeDataItem, } from "@api/useVisualizeData";
import AsideButtons, { CopyTableButton } from "@components/AsideButtons";
import VisualizeTableFilters from "@pages/Visualize/VisualizeTableFilters";
import TableSortingContext, { TableSortingContextType } from "@components/Table/TableSorting";
import { useFilterSort } from "@hooks/useFilterSort";
import { Sort } from "@models";
import { CHART_ID } from "@pages/Visualize/Visualize";
import { useVisualizeContext, VisualizeView } from "@pages/Visualize/VisualizeContext";
import { useSelectedKeywordsContext } from "@vms/SelectedKeywords/SelectedKeywordsContext";
import { useCopyTableDataWithFilters } from "@vms/CopyTable/useCopyTableDataWithFilters";

const VisualizeTable: FCX<{
    data: VisualizeData;
    selectedKeywords: VisualizeDataItem[];
    isLoading: boolean;
    setActiveItemId: Dispatch<SetStateAction<ID>>;
    setReportData: Dispatch<SetStateAction<VisualizeData>>;
}> = ({
    className,
    data: rawData,
    selectedKeywords,
    isLoading,
    setActiveItemId,
    setReportData,
    ...props
}) => {
    const { isSelectedKeywordsView } = useSelectedKeywordsContext();
    const {
        xMetricId,
        yMetricId,
        activeView,
    } = useVisualizeContext();

    const [search, setSearch] = useState<string>("");

    const isSearchApplied = useMemo(
        () => search.trim().length > 0,
        [search]
    );

    const filteredDataBySearch = useMemo(
        () => {
            if (!isSearchApplied) return rawData;

            return [
                ...rawData.filter(i => startsFromInLC(i.name ?? '', search.trim())),
                ...rawData.filter(i =>
                    !startsFromInLC(i.name ?? '', search.trim())
                    && includesInLC(i.name ?? '', search.trim())
                ),
            ];
        },
        [isSearchApplied, rawData, search]
    );

    const mapForSorting = useCallback(
        (data: VisualizeDataItem[]) => data.map(item => ({
            ...item,
            xMetric: item.metrics.find(m => m.id === xMetricId)?.sortValue ?? null,
            yMetric: item.metrics.find(m => m.id === yMetricId)?.sortValue ?? null,
        })),
        [xMetricId, yMetricId]
    );

    const dataForSorting = useMemo(
        () => mapForSorting(rawData),
        [rawData, mapForSorting]
    );

    const {
        data: sortedData,
        sortingKey,
        sortingDirection,
        setSortingKey,
        setSortingDirection,
        sort,
    } = useFilterSort(
        dataForSorting,
        'xMetric',
        Sort.Desc,
    );

    const tableDataRaw = useMemo(
        () => isSearchApplied ? filteredDataBySearch : sortedData,
        [isSearchApplied, filteredDataBySearch, sortedData]
    );

    const tableData = useMemo(
        () => tableDataRaw.map(item => ({
            ...item,
            metrics: [
                item.metrics.find(i => i.id === xMetricId)!,
                item.metrics.find(i => i.id === yMetricId)!,
            ],
        })),
        [tableDataRaw, xMetricId, yMetricId]
    );

    const sortContextValue = useMemo(
        (): TableSortingContextType => ({
            value: {
                id: sortingKey as string,
                direction: sortingDirection,
            },
            options: [
                { id: "name", name: "name", defaultDirection: Sort.Asc },
                { id: 'xMetric', name: 'xMetric' },
                { id: 'yMetric', name: 'yMetric' },
                { id: 'polarityRaw', name: 'polarityRaw', },
                { id: 'familiarFavoritesNum', name: 'familiarFavoritesNum', },
                { id: 'risingStarsNum', name: 'risingStarsNum', },
                { id: 'heatScoreNum', name: 'heatScoreNum' },
            ],
            setValue: ({ id, direction }) => {
                setSortingKey(id as keyof VisualizeDataItem);
                setSortingDirection(direction);
            },
        }),
        [sortingKey, sortingDirection, setSortingKey, setSortingDirection]
    );

    const onRowHover = useCallback(
        (index: number | null) => {
            const chartInstanceData = window.charts[CHART_ID]?.series?.[0]?.data;

            if (index === null) {
                setActiveItemId((prev) => {
                    if (prev !== undefined) {
                        const activeItemIndex = chartInstanceData?.findIndex(
                            (item: any) => item.id === prev
                        );
                        chartInstanceData?.[activeItemIndex]?.setState('normal');
                    }
                    return undefined;
                });
                return;
            }

            const item = tableData[index];
            if (item) {
                const activeItemIndex = chartInstanceData?.findIndex(
                    (i: any) => i.id === item.id
                );
                chartInstanceData?.[activeItemIndex]?.setState('hover');
                setActiveItemId(item.id);
            }
        },
        [tableData, setActiveItemId]
    );

    const configPayload = useMemo(
        (): VisualizeTableConfig => {

            return {
                data: tableData,
                search,
            };
        },
        [
            tableData,
            search,
        ]
    );

    const tableConfig = useVisualizeTableConfig(configPayload)

    const tableProviderValue = useMemo(() => ({
            data: tableConfig,
        }),
        [tableConfig]
    );

    const copyTableContent = useCopyTableDataWithFilters(tableConfig, tableData.length);

    // Everytime data changes, We will refresh the table using latest timestamp!
    const tableKey = useMemo(() => {
        return `${tableData.length}-${Date.now()}`;
    }, [tableData]);

    const reportData = useMemo(
        () => sort(mapForSorting(selectedKeywords)),
        [selectedKeywords, sort, mapForSorting]
    );

    useEffect(() => {
        setReportData(reportData);
    }, [reportData, setReportData]);

    useEffect(() => {
        setSearch('');
    }, [isSelectedKeywordsView]);

    useEffect(() => {
        if (activeView === null) {
            setSortingKey('xMetric');
            setSortingDirection(Sort.Desc);
        }
    }, [
        activeView,
        setSortingKey,
        setSortingDirection
    ]);

    useEffect(() => {
        if (activeView === VisualizeView.RisingStars) {
            setSortingKey('risingStarsNum');
            setSortingDirection(Sort.Desc);
        }
    }, [activeView, setSortingKey, setSortingDirection]);

    useEffect(() => {
        if (activeView === VisualizeView.FamiliarFavorites) {
            setSortingKey('familiarFavoritesNum');
            setSortingDirection(Sort.Desc);
        }
    }, [activeView, setSortingKey, setSortingDirection]);

    useEffect(() => {
        if (activeView === VisualizeView.Polarity) {
            setSortingKey('polarityRaw');
            setSortingDirection(Sort.Desc);
        }
    }, [activeView, setSortingKey, setSortingDirection]);

    return (
        <>
            <div className="Visualize__table-container">
                <div className="VisualizeFilters">
                    <VisualizeTableFilters
                        search={search}
                        setSearch={setSearch}
                        filteredResultsIds={tableData.map(i => i.id)}
                    />
                </div>
                <TableSortingContext.Provider value={sortContextValue}>
                    <TableColumnsContext.Provider value={tableProviderValue}>
                        <div
                            className={cn("VisualizeTable", className)}
                            {...props}
                        >
                            <VirtualizedTable
                                isLoading={isLoading}
                                key={tableKey}
                                className="CorrelationTable__table"
                                headHeight={48}
                                rowHeight={42}
                                rowsCount={tableData.length}
                                testId="tableCorrelationVirtualized"
                                useContentHeight
                                onRowHover={onRowHover}
                                showFallback
                            />
                        </div>
                    </TableColumnsContext.Provider>
                </TableSortingContext.Provider>
            </div>
            <AsideButtons>
                <CopyTableButton
                    data={copyTableContent}
                    testId="buttonHistoricTrendCopyTable"
                />
            </AsideButtons>
        </>
    )
}

export default memo(VisualizeTable);